import { mix } from '../icons/pix';

/**
 * Move icons v2: a square pixel layer of any size (16, 24, 32, 48), no DOM (so icons are drawn and
 * written to PNG in Node too). All drawing calls are in 24-grid coordinates and are rasterised at
 * the layer's own resolution (k = n / 24): lines, arcs and curves get finer at 32 and 48, and are
 * simplified at 16. A layer holds `#rrggbb` per pixel or null. Passes: `light` (rim light from the
 * upper right, shade at the lower left, so every shape is lit alike), `outline` (1 px dark outline
 * in the neighbour's own colour, so outlines are coloured, not black).
 */
export const N = 24;
export const INK = '#16130f';

export type Px = string | null;
export type Size = 16 | 24 | 32 | 48;

export class Layer {
  readonly d: Px[];
  readonly k: number;
  constructor(readonly n: number = 24) {
    this.d = new Array<Px>(n * n).fill(null);
    this.k = n / 24;
  }

  /** Detail level: 0 tiny (16), 1 normal (24), 2 fine (32), 3 finest (48). */
  get detail(): number {
    return this.n <= 16 ? 0 : this.n <= 24 ? 1 : this.n <= 32 ? 2 : 3;
  }

  private f(v: number): number {
    return (v + 0.5) * this.k - 0.5;
  }
  private tk(t: number): number {
    return Math.max(1, Math.round(t * this.k));
  }

  get(x: number, y: number): Px {
    return x < 0 || y < 0 || x >= this.n || y >= this.n ? null : this.d[y * this.n + x]!;
  }
  /** One pixel in raw layer coordinates. */
  raw(x: number, y: number, c: Px): void {
    x = Math.round(x);
    y = Math.round(y);
    if (x < 0 || y < 0 || x >= this.n || y >= this.n) return;
    this.d[y * this.n + x] = c;
  }
  /** One 24-grid pixel (a k x k block). */
  set(x: number, y: number, c: Px): void {
    x = Math.round(x);
    y = Math.round(y);
    const x0 = Math.round(x * this.k);
    const x1 = Math.max(x0 + 1, Math.round((x + 1) * this.k));
    const y0 = Math.round(y * this.k);
    const y1 = Math.max(y0 + 1, Math.round((y + 1) * this.k));
    for (let j = y0; j < y1; j++) for (let i = x0; i < x1; i++) this.raw(i, j, c);
  }
  rect(x: number, y: number, w: number, h: number, c: Px): void {
    const x0 = Math.round(x * this.k);
    const x1 = Math.max(x0 + 1, Math.round((x + w) * this.k));
    const y0 = Math.round(y * this.k);
    const y1 = Math.max(y0 + 1, Math.round((y + h) * this.k));
    for (let j = y0; j < y1; j++) for (let i = x0; i < x1; i++) this.raw(i, j, c);
  }
  private brush(px: number, py: number, T: number, c: Px): void {
    const o = Math.floor((T - 1) / 2);
    for (let j = 0; j < T; j++) for (let i = 0; i < T; i++) this.raw(px - o + i, py - o + j, c);
  }
  private rline(x0: number, y0: number, x1: number, y1: number, c: Px, T: number): void {
    x0 = Math.round(x0);
    y0 = Math.round(y0);
    x1 = Math.round(x1);
    y1 = Math.round(y1);
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    for (;;) {
      this.brush(x0, y0, T, c);
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x0 += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y0 += sy;
      }
    }
  }
  /** A line with a square brush of `t` 24-grid px. */
  line(x0: number, y0: number, x1: number, y1: number, c: Px, t = 1): void {
    this.rline(this.f(x0), this.f(y0), this.f(x1), this.f(y1), c, this.tk(t));
  }
  poly(pts: ReadonlyArray<readonly [number, number]>, c: Px, t = 1): void {
    for (let i = 0; i + 1 < pts.length; i++) this.line(pts[i]![0], pts[i]![1], pts[i + 1]![0], pts[i + 1]![1], c, t);
  }
  disc(cx: number, cy: number, r: number, c: Px): void {
    const X = this.f(cx);
    const Y = this.f(cy);
    const R = r * this.k;
    for (let y = Math.floor(Y - R); y <= Math.ceil(Y + R); y++)
      for (let x = Math.floor(X - R); x <= Math.ceil(X + R); x++)
        if ((x - X) ** 2 + (y - Y) ** 2 <= R * R + 0.25 * this.k) this.raw(x, y, c);
  }
  /** An arc of a circle from angle a0 to a1 (radians, y down). */
  arc(cx: number, cy: number, r: number, a0: number, a1: number, c: Px, t = 1): void {
    const X = this.f(cx);
    const Y = this.f(cy);
    const R = r * this.k;
    const T = this.tk(t);
    const steps = Math.max(8, Math.ceil(Math.abs(a1 - a0) * R * 2));
    for (let i = 0; i <= steps; i++) {
      const a = a0 + ((a1 - a0) * i) / steps;
      this.brush(Math.round(X + Math.cos(a) * R), Math.round(Y + Math.sin(a) * R), T, c);
    }
  }
  /** A quadratic curve through a control point. */
  curve(x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, c: Px, t = 1): void {
    const steps = 16 + Math.round(8 * this.k);
    const T = this.tk(t);
    let last: [number, number] | null = null;
    for (let i = 0; i <= steps; i++) {
      const u = i / steps;
      const x = this.f((1 - u) ** 2 * x0 + 2 * (1 - u) * u * cx + u * u * x1);
      const y = this.f((1 - u) ** 2 * y0 + 2 * (1 - u) * u * cy + u * u * y1);
      if (last) this.rline(last[0], last[1], x, y, c, T);
      last = [x, y];
    }
  }
  /** ASCII art: `key` maps a character to a colour; '.' and ' ' are empty. At 48 the art is rounded with Scale2x. */
  sprite(x: number, y: number, rows: readonly string[], key: Readonly<Record<string, string>>, flip = false): void {
    const h = rows.length;
    const w = Math.max(...rows.map((r) => r.length));
    let grid: Px[][] = rows.map((row) =>
      Array.from({ length: w }, (_, i) => {
        const ch = row[flip ? row.length - 1 - i : i] ?? '.';
        return key[ch] ?? null;
      }),
    );
    if (this.k >= 1.99) {
      const g2: Px[][] = Array.from({ length: h * 2 }, () => new Array<Px>(w * 2).fill(null));
      const at = (i: number, j: number): Px => (i < 0 || j < 0 || i >= w || j >= h ? null : grid[j]![i]!);
      for (let j = 0; j < h; j++)
        for (let i = 0; i < w; i++) {
          const P = at(i, j);
          const A = at(i, j - 1);
          const B = at(i + 1, j);
          const Cc = at(i - 1, j);
          const D = at(i, j + 1);
          const e = (a: Px, b: Px) => a === b;
          g2[2 * j]![2 * i] = e(Cc, A) && !e(Cc, D) && !e(A, B) ? A : P;
          g2[2 * j]![2 * i + 1] = e(A, B) && !e(A, Cc) && !e(B, D) ? B : P;
          g2[2 * j + 1]![2 * i] = e(D, Cc) && !e(D, B) && !e(Cc, A) ? Cc : P;
          g2[2 * j + 1]![2 * i + 1] = e(B, D) && !e(B, A) && !e(D, Cc) ? D : P;
        }
      grid = g2;
      const ox = Math.round(x * this.k);
      const oy = Math.round(y * this.k);
      grid.forEach((row, j) => row.forEach((c, i) => c && this.raw(ox + i, oy + j, c)));
      return;
    }
    grid.forEach((row, j) => row.forEach((c, i) => c && this.set(x + i, y + j, c)));
  }
  /** A filled polygon (even-odd scanline), vertices in 24-grid px. */
  fillPoly(pts: ReadonlyArray<readonly [number, number]>, c: Px): void {
    const P = pts.map(([a, b]) => [a * this.k, b * this.k] as const);
    for (let y = 0; y < this.n; y++) {
      const xs: number[] = [];
      for (let i = 0; i < P.length; i++) {
        const [ax, ay] = P[i]!;
        const [bx, by] = P[(i + 1) % P.length]!;
        const yy = y + 0.5;
        if ((ay <= yy && by > yy) || (by <= yy && ay > yy)) xs.push(ax + ((yy - ay) / (by - ay)) * (bx - ax));
      }
      xs.sort((a, b) => a - b);
      for (let i = 0; i + 1 < xs.length; i += 2)
        for (let x = Math.round(xs[i]!); x < Math.round(xs[i + 1]!); x++) this.raw(x, y, c);
    }
  }
  /** Copies the opaque pixels of another layer on top. */
  stamp(o: Layer): void {
    for (let i = 0; i < this.d.length; i++) if (o.d[i]) this.d[i] = o.d[i]!;
  }
  mirror(): Layer {
    const o = new Layer(this.n);
    for (let y = 0; y < this.n; y++) for (let x = 0; x < this.n; x++) o.d[y * this.n + (this.n - 1 - x)] = this.d[y * this.n + x]!;
    return o;
  }
  /** Rim light on the upper-right edge, shade on the lower-left edge (two bands at 48). */
  light(): void {
    const n = this.n;
    const bands = this.detail >= 3 ? 2 : 1;
    for (let b = 0; b < bands; b++) {
      const src = this.d.slice();
      const at = (x: number, y: number): Px => (x < 0 || y < 0 || x >= n || y >= n ? null : src[y * n + x]!);
      for (let y = 0; y < n; y++)
        for (let x = 0; x < n; x++) {
          const c = at(x, y);
          if (!c) continue;
          const ur = !at(x + 1, y - 1) && (!at(x + 1, y) || !at(x, y - 1));
          const ll = !at(x - 1, y + 1) && (!at(x - 1, y) || !at(x, y + 1));
          const sh = b === 0 ? 1 : 0.5;
          if (ur) this.d[y * n + x] = mix(c, '#ffffff', 0.32 * sh);
          else if (ll) this.d[y * n + x] = mix(c, INK, 0.3 * sh);
        }
    }
  }
  /** A 1 px outline in a dark tone of the touching colour (8-neighbourhood: corners close too). */
  outline(): void {
    const n = this.n;
    const src = this.d.slice();
    for (let y = 0; y < n; y++)
      for (let x = 0; x < n; x++) {
        if (src[y * n + x]) continue;
        let best: string | null = null;
        for (const [dx, dy] of [[0, 1], [-1, 0], [1, 0], [0, -1], [-1, 1], [1, 1], [-1, -1], [1, -1]] as const) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue;
          const c = src[ny * n + nx];
          if (c && (dx === 0 || dy === 0 || !best)) {
            best = c;
            if (dx === 0 || dy === 0) break;
          }
        }
        if (best) this.d[y * n + x] = mix(best, INK, 0.82);
      }
  }
}

/** RGBA bytes of a layer. */
export function rgba(l: Layer): Uint8ClampedArray {
  const out = new Uint8ClampedArray(l.n * l.n * 4);
  l.d.forEach((c, i) => {
    if (!c) return;
    out[i * 4] = parseInt(c.slice(1, 3), 16);
    out[i * 4 + 1] = parseInt(c.slice(3, 5), 16);
    out[i * 4 + 2] = parseInt(c.slice(5, 7), 16);
    out[i * 4 + 3] = 255;
  });
  return out;
}
